import { useEffect, useState, useContext, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	obtenerDatosBarras,
	obtenerDatosTorta,
	obtenerDiferencias,
} from "./chartsLogic/datacharts";
import { FiltroMeses } from "./chartsLogic/filterMonth";
import { BarCharts, PieChartNE, PieChartSP, TarjetaDiferencia } from "./chartsLogic/charts";
import {
	Box,
	Button,
	Grid,
	Typography,
	Card,
	CardContent,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from "@mui/material";
import { formatYm } from "./chartsLogic/utils";
import { GlobalContext } from "../../../GlobalContext";
import { obtenerDatos } from "../../../api/config";

const shiftLabels = (t) => ({
	Diurno: t("shifts.diurno"),
	Nocturno: t("shifts.nocturno"),
	Madrugada: t("shifts.madrugada"),
});

const DAY_MAP = {
	0: "lunes",
	1: "martes",
	2: "miercoles",
	3: "jueves",
	4: "viernes",
	5: "sabado",
	6: "domingo",
};

const EMPTY_WEEK = {
	lunes: "-",
	martes: "-",
	miercoles: "-",
	jueves: "-",
	viernes: "-",
	sabado: "-",
	domingo: "-",
};

const formatHour = (value) => {
	if (!value) return "";
	return String(value).slice(0, 5);
};

const formatPeriodsList = (periods = []) => {
	if (!Array.isArray(periods) || periods.length === 0) return "-";

	const formatted = periods
		.map((period) => {
			const start = formatHour(period?.start_time);
			const end = formatHour(period?.end_time);

			if (start && end) return `${start} - ${end}`;
			if (start) return start;
			if (end) return end;
			return null;
		})
		.filter(Boolean);

	return formatted.length > 0 ? formatted.join(" / ") : "-";
};

const normalizeContractsByType = (contracts = [], t) => {
	return contracts.reduce(
		(acc, contract) => {
			const tipo = contract.tipo_contrato || t("new_charts.unknown_contract_type");
			const days = { ...EMPTY_WEEK };

			const dataDays = Array.isArray(contract.contracts_per_day) ? contract.contracts_per_day : [];
			const dataPeriods = Array.isArray(contract.contracts_per_periods)
				? contract.contracts_per_periods
				: [];

			dataDays.forEach((dayItem) => {
				const dayKey = DAY_MAP[dayItem.day];
				if (!dayKey) return;

				if (dayItem.value !== 1) {
					days[dayKey] = "-";
					return;
				}

				const periodGroup = dataPeriods.find(
					(periodItem) =>
						periodItem.config_contracts_per_days_id === dayItem.config_contracts_per_days_id,
				);

				days[dayKey] = formatPeriodsList(periodGroup?.periods || []);
			});

			const row = {
				id: contract.id,
				contrato: contract.nombre || contract.kronos_name || "-",
				...days,
			};

			if (!acc[tipo]) acc[tipo] = [];
			acc[tipo].push(row);

			return acc;
		},
		{
			Diurno: [],
			Nocturno: [],
			Madrugada: [],
		},
	);
};

const NewCharts = ({ data = [], loading = false }) => {
	const { t } = useTranslation();
	const { site_id } = useContext(GlobalContext);

	const [mesesSeleccionados, setMesesSeleccionados] = useState([]);
	const [datosTortaNE, setDatosTortaNE] = useState([]);
	const [datosTortaSP, setDatosTortaSP] = useState([]);
	const [datosBarras, setDatosBarras] = useState([]);
	const [diferencias, setDiferencias] = useState({ Diurno: 0, Nocturno: 0, Madrugada: 0 });
	const [mesesFiltrados, setMesesFiltrados] = useState([]);

	const [openDetailModal, setOpenDetailModal] = useState(false);
	const [selectedSection, setSelectedSection] = useState("");
	const [contractDetails, setContractDetails] = useState({
		Diurno: [],
		Nocturno: [],
		Madrugada: [],
	});
	const [contractsLoading, setContractsLoading] = useState(false);

	const datosTortaNERef = useRef(datosTortaNE);
	const datosTortaSPRef = useRef(datosTortaSP);
	const datosBarrasRef = useRef(datosBarras);

	const labels = useMemo(() => shiftLabels(t), [t]);

	const totalNE = useMemo(
		() => (datosTortaNE || []).reduce((acc, item) => acc + (Number(item?.valor) || 0), 0),
		[datosTortaNE],
	);

	const totalSP = useMemo(
		() => (datosTortaSP || []).reduce((acc, item) => acc + (Number(item?.valor) || 0), 0),
		[datosTortaSP],
	);

	const totalPeopleLabel = useMemo(() => t("new_charts.total_people"), [t]);

	useEffect(() => {
		const out = data?.[0]?.output_planning_monthly || [];
		if (!out.length) {
			setMesesFiltrados([]);
			setMesesSeleccionados([]);
			return;
		}

		const mesesUnicos = [...new Set(out.map((i) => `${i.anno}-${String(i.mes).padStart(2, "0")}`))];
		const mesesI18n = mesesUnicos.map((ym) => ({
			value: ym,
			label: formatYm(ym, t, { short: true }),
		}));

		setMesesFiltrados(mesesI18n);
		setMesesSeleccionados(mesesI18n.map((m) => m.value));
	}, [data, t]);

	const TotalPill = ({ total }) => (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 2,
				py: 0.75,
				borderRadius: 999,
				bgcolor: "grey.200",
			}}
		>
			<Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
				{totalPeopleLabel}
			</Typography>
			<Typography variant="body2" sx={{ fontWeight: 700 }}>
				{total}
			</Typography>
		</Box>
	);

	const manejarSeleccionTodos = () => {
		if (mesesSeleccionados.length === mesesFiltrados.length) {
			setMesesSeleccionados([]);
		} else {
			setMesesSeleccionados(mesesFiltrados.map((mes) => mes.value));
		}
	};

	const handleOpenDetails = async (seccion) => {
		setSelectedSection(seccion);
		setOpenDetailModal(true);

		if (contractDetails[seccion]?.length > 0) return;

		try {
			setContractsLoading(true);
			const response = await obtenerDatos(`/contracts/get/${site_id}`);
			const normalized = normalizeContractsByType(response || [], t);

			setContractDetails({
				Diurno: normalized.Diurno || [],
				Nocturno: normalized.Nocturno || [],
				Madrugada: normalized.Madrugada || [],
			});
		} catch (error) {
			console.error("Error obteniendo contratos:", error);
		} finally {
			setContractsLoading(false);
		}
	};

	const handleCloseDetails = () => {
		setOpenDetailModal(false);
	};

	useEffect(() => {
		if (data.length > 0 && mesesSeleccionados.length > 0) {
			const nuevasDiferencias = obtenerDiferencias(data, mesesSeleccionados);

			const secciones = ["Diurno", "Nocturno", "Madrugada"];
			const diferenciasConDefaults = secciones.reduce((acc, seccion) => {
				acc[seccion] = nuevasDiferencias[seccion] ?? null;
				return acc;
			}, {});

			if (JSON.stringify(diferencias) !== JSON.stringify(diferenciasConDefaults)) {
				setDiferencias(diferenciasConDefaults);
			}
		}
	}, [data, mesesSeleccionados, diferencias]);

	useEffect(() => {
		if (data.length > 0 && mesesSeleccionados.length > 0) {
			const { datosTortaNE: nuevosDatosTortaNE, datosTortaSP: nuevosDatosTortaSP } = obtenerDatosTorta(
				data,
				mesesSeleccionados,
			);

			const nuevosDatosBarras = obtenerDatosBarras(data, mesesSeleccionados);

			if (JSON.stringify(datosTortaNERef.current) !== JSON.stringify(nuevosDatosTortaNE)) {
				setDatosTortaNE(nuevosDatosTortaNE);
				datosTortaNERef.current = nuevosDatosTortaNE;
			}

			if (JSON.stringify(datosTortaSPRef.current) !== JSON.stringify(nuevosDatosTortaSP)) {
				setDatosTortaSP(nuevosDatosTortaSP);
				datosTortaSPRef.current = nuevosDatosTortaSP;
			}

			if (JSON.stringify(datosBarrasRef.current) !== JSON.stringify(nuevosDatosBarras)) {
				setDatosBarras(nuevosDatosBarras);
				datosBarrasRef.current = nuevosDatosBarras;
			}
		}
	}, [data, mesesSeleccionados]);

	return (
		<div>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						p: 2,
						width: "80%",
						alignContent: "center",
						justifyContent: "center",
						alignItems: "center",
						textAlign: "center",
					}}
				>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12}>
							<Typography gutterBottom variant="h5">
								{t("new_charts.title")}
							</Typography>
						</Grid>

						<Grid item xs={12} md={9}>
							<FiltroMeses
								mesesSeleccionados={mesesSeleccionados}
								setMesesSeleccionados={setMesesSeleccionados}
								mesesFiltrados={mesesFiltrados}
								optionsLoading={loading}
							/>
						</Grid>

						<Grid item xs={12} md={3}>
							<Button fullWidth variant="outlined" onClick={manejarSeleccionTodos}>
								{mesesSeleccionados.length === mesesFiltrados.length
									? t("new_charts.unselect_all")
									: t("new_charts.select_all")}
							</Button>
						</Grid>

						{["Diurno", "Nocturno", "Madrugada"].map((seccion) => (
							<Grid item xs={12} md={4} key={seccion}>
								<TarjetaDiferencia
									titulo={`${t("new_charts.diferent")} ${labels[seccion]}`}
									diferencia={
										diferencias[seccion] !== undefined && diferencias[seccion] !== null
											? diferencias[seccion]
											: t("new_charts.no_data")
									}
									seccion={seccion}
									cargando={loading}
									t={t}
								/>
							</Grid>
						))}

						<Grid item xs={12} md={6}>
							<Card sx={{ mb: 2, height: 400 }}>
								<CardContent
									sx={{
										width: "100%",
										height: "100%",
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: "background.paper",
									}}
								>
									<Box
										sx={{
											width: "100%",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											mb: 1,
										}}
									>
										<Typography variant="h6" sx={{ fontWeight: 700 }}>
											{t("new_charts.data_real")}
										</Typography>

										<TotalPill total={totalNE} />
									</Box>

									{datosTortaNE && datosTortaNE.length > 0 ? (
										<PieChartNE data={datosTortaNE} cargando={loading} t={t} />
									) : (
										<p>{t("new_charts.no_data")}</p>
									)}
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12} md={6}>
							<Card sx={{ mb: 2, height: 400 }}>
								<CardContent
									sx={{
										width: "100%",
										height: "100%",
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: "background.paper",
									}}
								>
									<Box
										sx={{
											width: "100%",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											mb: 1,
										}}
									>
										<Typography variant="h6" sx={{ fontWeight: 700 }}>
											{t("new_charts.data_sp")}
										</Typography>

										<TotalPill total={totalSP} />
									</Box>

									{datosTortaSP && datosTortaSP.length > 0 ? (
										<PieChartSP data={datosTortaSP} cargando={loading} t={t} />
									) : (
										<p>{t("new_charts.no_data")}</p>
									)}
								</CardContent>
							</Card>
						</Grid>

						{["Diurno", "Nocturno", "Madrugada"].map((seccion) => (
							<Grid item xs={12} key={seccion}>
								<Card sx={{ mb: 2, height: 400 }}>
									<CardContent
										sx={{
											width: "100%",
											height: "100%",
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<Box
											sx={{
												width: "100%",
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 1,
											}}
										>
											<Typography variant="h6">
												{t("new_charts.hired")} {labels[seccion]}
											</Typography>

											<Button variant="outlined" size="small" onClick={() => handleOpenDetails(seccion)}>
												{t("aux.viewDetail")}
											</Button>
										</Box>

										{datosBarras[seccion]?.length > 0 ? (
											<BarCharts data={datosBarras[seccion]} seccion={seccion} cargando={loading} t={t} />
										) : (
											<p>{t("new_charts.no_data")}</p>
										)}
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>
				</Box>
			</Box>

			<Dialog open={openDetailModal} onClose={handleCloseDetails} fullWidth maxWidth="lg">
				<DialogTitle sx={{ fontWeight: 700 }}>
					{t("new_charts.contract_gap_detail_title", {
						section: labels[selectedSection] || selectedSection,
					})}
				</DialogTitle>

				<DialogContent>
					<TableContainer
						component={Paper}
						sx={{
							mt: 2,
							borderRadius: 4,
							overflow: "hidden",
							border: "1px solid",
							borderColor: "divider",
							boxShadow: "none",
							"& .MuiTableCell-root": {
								borderBottom: "1px solid",
								borderColor: "divider",
							},
							"& .MuiTableHead-root": {
								backgroundColor: "divider",
							},
							"& .MuiTableHead-root .MuiTableCell-root": {
								fontWeight: 700,
							},
						}}
					>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>{t("new_charts.contract")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.lunes")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.martes")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.miercoles")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.jueves")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.viernes")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.sabado")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.domingo")}</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{contractsLoading ? (
									<TableRow>
										<TableCell colSpan={8} align="center">
											{t("aux.loading")}
										</TableCell>
									</TableRow>
								) : contractDetails[selectedSection]?.length > 0 ? (
									contractDetails[selectedSection].map((row) => (
										<TableRow key={row.id}>
											<TableCell>{row.contrato}</TableCell>
											<TableCell>{row.lunes}</TableCell>
											<TableCell>{row.martes}</TableCell>
											<TableCell>{row.miercoles}</TableCell>
											<TableCell>{row.jueves}</TableCell>
											<TableCell>{row.viernes}</TableCell>
											<TableCell>{row.sabado}</TableCell>
											<TableCell>{row.domingo}</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={8} align="center">
											{t("new_charts.no_data")}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</DialogContent>

				<DialogActions sx={{ p: 2 }}>
					<Button variant="outlined" onClick={handleCloseDetails}>
						{t("buttons.cancel")}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export { NewCharts };
